// Where a freshly-authenticated recruiter lands.
//
// Extracted because getting this wrong is invisible and expensive. The guided
// tour is mounted by exactly ONE route — /admin/home (routes/admin/Home.tsx) —
// so any journey that lands a brand-new recruiter anywhere else means they
// never see it at all, and the `onboarding_tour_*` columns stay NULL forever
// because only the tour itself writes them. Nothing errors, nothing logs, and
// the account looks like the feature simply doesn't exist.
//
// That is exactly what shipped. There is no `<Route path="/">`, so the bare
// domain fell through App.tsx's `*` catch-all to /admin, RequireAuth bounced it
// to /login with `state.from = "/admin"`, and the honour-`from` rule below sent
// them right back to the tests dashboard. Confirmed against production on
// 2026-08-19: loading https://interview-tool.click/ ends at /login carrying
// `{ usr: { from: "/admin" } }`. Typing the domain — the single most ordinary
// way to reach the app — was enough to lose the whole onboarding.
//
// So: honouring `from` is right for a returning recruiter (a deep link should
// survive a sign-in) and wrong for a first run, where landing on Home is the
// feature. First run wins.

import type { RecruiterSession } from "./auth";
import { can } from "./roles";

export const ADMIN_HOME = "/admin/home";

/** Just the session fields this module reads — keeps callers free to pass a
 *  partial (e.g. a cache read) without widening it to a full session. */
export type OnboardingGate = Pick<
  RecruiterSession,
  "onboarding_tour_completed_at" | "onboarding_tour_skipped_at"
> &
  // FEAT-28: optional, so a caller holding only the two timestamps still works.
  Partial<Pick<RecruiterSession, "role" | "permissions">>;

/**
 * Has this recruiter never been offered the guided tour?
 *
 * Mirrors `useTour`'s own gate, and compares against `null` for the same
 * reason: GET /api/auth/me emits these keys explicitly, and `undefined === null`
 * is false — so an ABSENT field must read as "not first run" rather than
 * silently forcing everyone to Home. A missing session reads the same way.
 */
export function isFirstRun(
  session: OnboardingGate | null | undefined,
): boolean {
  if (!session) return false;
  // FEAT-28: the tour never opens for a role without `workspace.write` (a
  // hiring manager — every stop is an authoring surface, see `useTour`), so
  // for them the two columns stay NULL FOREVER. Reading that as "first run"
  // would re-point the bare /admin at Home on every page load: they could
  // never open the Tests list by its URL. Only a session that SAYS its role
  // is checked — a bare pair of timestamps keeps the old answer.
  if (
    session.role !== undefined &&
    !can({ role: session.role, permissions: session.permissions }, "workspace.write")
  ) {
    return false;
  }
  return (
    session.onboarding_tour_completed_at === null &&
    session.onboarding_tour_skipped_at === null
  );
}

/**
 * Is this the bare /admin entry point, rather than a deliberate destination?
 *
 * Only the generic entry may be re-pointed at Home for a first run. Treating
 * every /admin/* path as re-pointable made RequireAuth hijack deep links on
 * every fresh page load — including the two routes that own the in-context
 * guides — because that guard remounts on reload, not once per session.
 */
export function isBareAdminEntry(pathname: string): boolean {
  return pathname === "/admin" || pathname === "/admin/";
}

/**
 * Resolve the post-sign-in destination on the apex host.
 *
 * A saved `from` is honoured ONLY when it's an admin destination — never bounce
 * someone onto a candidate /t/ link or any other non-admin path (FIND-026) —
 * and never on a first run, which always lands on Home.
 */
/**
 * The `from` a router `state` carries, when it is a path under `prefix` —
 * the one way both portals read it (SIMP-36; the ops arm in Login.tsx used
 * to re-spell the cast and the check one prefix apart). Null otherwise, so
 * a candidate /t/ link or a foreign string is never bounced to.
 */
export function savedFrom(state: unknown, prefix: string): string | null {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === "string" && from.startsWith(prefix) ? from : null;
}

export function resolveAdminTarget(
  state: unknown,
  session?: OnboardingGate | null,
): string {
  // FEAT-47: an AI assistant's sign-in sent them here to approve a
  // connection (`/connect/authorize?rid=…`); the request expires in ten
  // minutes, so it wins even over a first run — the tour waits.
  const connect = savedFrom(state, "/connect/");
  if (connect) return connect;
  if (isFirstRun(session)) return ADMIN_HOME;
  return savedFrom(state, "/admin") ?? ADMIN_HOME;
}
